/**
 * Turns a `ProtocolFile` reference into something a browser can open.
 *
 * Two kinds of document reach the "פרוטוקולים" screen, and they are fetched
 * differently: a hospital's own upload lives in blob storage and comes back
 * through the public config-files route, while the AAP guideline ships inside
 * the bundle as a Vite asset.
 *
 * This module — not phototherapyConfig — owns the asset import, so the settings
 * module stays a plain data module that the node-environment unit tests can
 * import without a bundler resolving a PDF for them.
 */
import { configFileUrl } from '../../services/configFilesApi';
import { BUILTIN_AAP_GUIDELINE, type ProtocolFile } from './phototherapyConfig';
import aapGuidelinePdf from './data/new-graphs-2026/AAP-hyperbili.pdf';

/** Documents shipped with the app, by `ProtocolFile.builtin` key. */
const BUILTIN_FILES: Record<string, { href: string; downloadName: string }> = {
  [BUILTIN_AAP_GUIDELINE]: {
    href: aapGuidelinePdf,
    downloadName: 'AAP-2022-hyperbilirubinemia.pdf',
  },
};

/** Where the tile links to, or undefined for a reference nothing can resolve. */
export const protocolFileHref = (file: ProtocolFile): string | undefined => {
  if (file.builtin) return BUILTIN_FILES[file.builtin]?.href;
  return file.path ? configFileUrl(file.path) : undefined;
};

/** The name the browser saves the file under. */
export const protocolFileDownloadName = (file: ProtocolFile): string => {
  if (file.builtin) return BUILTIN_FILES[file.builtin]?.downloadName || `${file.title}.pdf`;
  const stored = file.path ? file.path.split('/').pop() || '' : '';
  const extension = stored.includes('.') ? stored.slice(stored.lastIndexOf('.')) : '';
  // The title is what the user named the document; the stored blob name is
  // sanitized ASCII plus a timestamp and would be meaningless in a downloads
  // folder. Only the extension is worth keeping from it.
  return `${file.title || 'protocol'}${extension}`;
};
